import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useTheme } from 'hooks/useTheme';
import config from 'data/config';

interface BuildData {
  site: { buildTime: string };
}

/** Format a date string as "May 2, 2026 · 3:41 PM" */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** How long ago was the build? */
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) { return 'just now'; }
  if (mins < 60) { return mins + 'm ago'; }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) { return hrs + 'h ago'; }
  return Math.floor(hrs / 24) + 'd ago';
}

/** Detect page type from current URL path */
function detectPageType(): { type: string; slug: string } | null {
  if (typeof window === 'undefined') { return null; }
  const path = window.location.pathname;
  const postMatch = path.match(/^\/post\/([^/]+)/);
  if (postMatch) { return { type: 'Post', slug: postMatch[1] }; }
  const projectMatch = path.match(/^\/projects?\/([^/]+)/);
  if (projectMatch) { return { type: 'Project', slug: projectMatch[1] }; }
  return null;
}

const DevRibbon: React.FC = () => {
  const { theme } = useTheme();
  const [activeSection, setActiveSection] = useState('—');
  const [visible, setVisible] = useState(true);
  const [pageInfo, setPageInfo] = useState<{ type: string; slug: string } | null>(null);

  const data = useStaticQuery<BuildData>(graphql`
    query DevRibbonBuildTime {
      site { buildTime }
    }
  `);

  useEffect(() => {
    setPageInfo(detectPageType());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') { return; }
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis.length > 0) {
          const el = vis[0].target;
          const label = el.getAttribute('data-section-label') || el.id || '—';
          setActiveSection(label.trim());
        }
      },
      { rootMargin: '-20% 0% -60% 0%', threshold: 0 }
    );
    const sections = document.querySelectorAll('[data-section-label], section[id]');
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  if (process.env.NODE_ENV !== 'development') { return null; }

  const accents = [
    { name: 'primary', color: config.theme.primary },
    { name: 'secondary', color: config.theme.secondary },
    { name: 'tertiary', color: config.theme.tertiary },
    { name: 'success', color: config.theme.success },
    { name: 'error', color: config.theme.error },
  ];

  const buildTime = data?.site?.buildTime;

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        className="fixed bottom-3 left-3 z-[9998] w-6 h-6 rounded-full bg-primary/80 text-white text-[10px] font-bold flex items-center justify-center cursor-pointer border-none opacity-40 hover:opacity-100 transition-opacity"
        title="Show dev ribbon"
      >
        D
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] flex items-center gap-4 px-4 py-1.5 text-[10px] font-mono bg-black/90 text-white/70 backdrop-blur-sm border-t border-white/10 overflow-x-auto">

      {/* Section */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-white/40 uppercase tracking-wider">§</span>
        <span className="font-bold text-white">{activeSection}</span>
      </div>

      <div className="w-px h-3 bg-white/20 flex-shrink-0" />

      {/* Theme — dot indicator, always white text */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-white/40 uppercase tracking-wider">Theme</span>
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: theme === 'dark' ? config.theme.secondary : config.theme.primary }}
        />
        <span className="font-bold text-white">{theme}</span>
      </div>

      <div className="w-px h-3 bg-white/20 flex-shrink-0" />

      {/* Accent swatches */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-white/40 uppercase tracking-wider">Colors</span>
        {accents.map((a) => (
          <div
            key={a.name}
            title={a.name + ' · ' + a.color}
            className="w-3 h-3 rounded-sm border border-white/20 flex-shrink-0"
            style={{ background: a.color }}
          />
        ))}
      </div>

      {/* Page type info (post/project pages only) */}
      {pageInfo && (
        <>
          <div className="w-px h-3 bg-white/20 flex-shrink-0" />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-white/40 uppercase tracking-wider">{pageInfo.type}</span>
            <span className="font-bold text-white truncate max-w-[200px]">{pageInfo.slug}</span>
          </div>
        </>
      )}

      {/* WP data freshness */}
      {buildTime && (
        <>
          <div className="w-px h-3 bg-white/20 flex-shrink-0" />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-white/40 uppercase tracking-wider">Built</span>
            <span
              className="font-bold text-white"
              title={'WP data synced at build: ' + formatBuildTime(buildTime)}
            >
              {timeAgo(buildTime)}
            </span>
          </div>
        </>
      )}

      <div className="flex-1" />

      <button
        onClick={() => setVisible(false)}
        className="text-white/30 hover:text-white/70 transition-colors cursor-pointer bg-transparent border-none text-[10px] font-mono flex-shrink-0"
      >
        hide
      </button>
    </div>
  );
};

export default DevRibbon;
